import { Grid, MenuItem, menuItemClasses, SelectChangeEvent } from '@mui/material';
import { useFormikContext } from 'formik';
import { memo, useMemo } from 'react';

import DownIcon from '../../../../../../../assets/icons/Icon_ArrowDown.svg?react';
import { FlightWingspanCodeEnum } from '../../../../../../../backend';
import { ResourcesPageFormValues } from '../../../../../../pages/ResourcesPage';
import { StyledMuiTextField, StyledSelect } from '../ResourceFieldsLineProperties.style';
import { isParkingProperties } from './ResourceFieldsLine.utils';
import { useReplaceNumberAt, useReplaceSelectAt, useReplaceValueAt } from './useReplaceValueAt';

export const ResourceFieldsLineParkingProperties = memo(function ResourceFieldsLineParkingProperties({
    index,
    arrayReplace,
}: {
    index: number;
    arrayReplace: (index: number, value: any) => void;
}) {
    const { values, initialValues, errors } = useFormikContext<ResourcesPageFormValues>();

    const handleChange = useReplaceValueAt(index, arrayReplace);

    const handleNumberChange = useReplaceNumberAt(index, arrayReplace);

    const handleSelectChange = useReplaceSelectAt(index, arrayReplace);

    const content = useMemo(() => values.resources[index].content, [index, values.resources]);
    const initialContent = useMemo(
        () => (values.resources[index].id ? initialValues.resources[index].content : null),
        [index, initialValues.resources, values.resources],
    );

    return isParkingProperties(content) ? (
        <>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.occupationOffset}
                    modified={
                        values.resources[index].id && isParkingProperties(initialContent)
                            ? content.occupationOffset !== initialContent.occupationOffset
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0 },
                    }}
                    error={!!errors[`resources[${index}]['occupationOffset']`]}
                    helperText={errors[`resources[${index}]['occupationOffset']`]}
                    onChange={e => handleNumberChange('occupationOffset', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.length}
                    modified={
                        values.resources[index].id && isParkingProperties(initialContent)
                            ? content.length !== initialContent.length
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0, step: 0.1 },
                    }}
                    error={!!errors[`resources[${index}]['length']`]}
                    helperText={errors[`resources[${index}]['length']`]}
                    onChange={e => handleNumberChange('length', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    type={'number'}
                    value={content.width}
                    modified={
                        values.resources[index].id && isParkingProperties(initialContent)
                            ? content.width !== initialContent.width
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0, step: 0.1 },
                    }}
                    error={!!errors[`resources[${index}]['width']`]}
                    helperText={errors[`resources[${index}]['width']`]}
                    onChange={e => handleNumberChange('width', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledSelect
                    value={content.codeLetter}
                    onChange={(e: SelectChangeEvent<string>) => handleSelectChange('codeLetter', e)}
                    IconComponent={DownIcon}
                    modified={
                        values.resources[index].id && isParkingProperties(initialContent)
                            ? content.codeLetter !== initialContent.codeLetter
                            : undefined
                    }
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                [`& .${menuItemClasses.root}`]: {
                                    color: theme => theme.palette.primary.contrastText,
                                },
                            },
                        },
                    }}
                >
                    <MenuItem key={'empty'} value={undefined}>
                        {''}
                    </MenuItem>
                    {Object.keys(FlightWingspanCodeEnum).map(code => (
                        <MenuItem key={code} value={code}>
                            {code}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    value={content.parkingZone}
                    modified={
                        values.resources[index].id && isParkingProperties(initialContent)
                            ? content.parkingZone !== initialContent.parkingZone
                            : undefined
                    }
                    onChange={e => handleChange('parkingZone', e)}
                />
            </Grid>
        </>
    ) : null;
});
